/**
 * ReplyRatePanel — compact reply-rate dashboard boven de Unified Inbox.
 * Toont verzonden vs. beantwoord per kanaal en per signaaltype, zodat je
 * ziet welke aanleidingen (signalen) het beste converteren.
 * Data: GET /outreach/analytics/reply-rates
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { BarChart3, ChevronDown, ChevronUp } from "lucide-react";

const CHANNEL_LABELS = {
    linkedin: "LinkedIn", linkedin_connect: "LinkedIn Connect",
    email: "E-mail", whatsapp: "WhatsApp", onbekend: "—",
};

/* Model-vergelijking: kleur + label per draft-model-familie */
const MODEL_META = {
    claude: { label: "Claude", color: "#d97706" },
    kimi: { label: "Kimi", color: "#14e0c8" },
    template: { label: null, color: "#8b8b96" },   // label via i18n
    handmatig: { label: null, color: "#8b8b96" },  // label via i18n
};

function prettyKey(key, kind) {
    if (kind === "channel") return CHANNEL_LABELS[key] || key;
    if (kind === "model") {
        if (key === "template") return i18n.t("replyrates.model_template");
        if (key === "handmatig") return i18n.t("replyrates.model_manual");
        return MODEL_META[key]?.label || key.charAt(0).toUpperCase() + key.slice(1);
    }
    if (key === "geen_signaal") return i18n.t("replyrates.no_signal");
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}

function RateRows({ rows, kind, testid }) {
    if (!rows || rows.length === 0) {
        return <div className="text-[11px] text-[var(--jr-text-3)] py-2">{i18n.t("replyrates.no_data")}</div>;
    }
    return (
        <div className="space-y-1.5" data-testid={testid}>
            {rows.map((r) => {
                const barColor = kind === "model" ? (MODEL_META[r.key]?.color || "#22c55e") : "#22c55e";
                return (
                    <div key={r.key} className="flex items-center gap-2" data-testid={`${testid}-${r.key}`}>
                        <span className="text-[11px] text-[var(--jr-text-2)] w-32 truncate inline-flex items-center gap-1.5" title={prettyKey(r.key, kind)}>
                            {kind === "model" && (
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: barColor }} />
                            )}
                            {prettyKey(r.key, kind)}
                        </span>
                        <div className="flex-1 h-1.5 rounded-full bg-white/6 overflow-hidden">
                            <div className="h-full rounded-full transition-all"
                                style={{ width: `${Math.min(r.rate, 100)}%`, background: barColor }} />
                        </div>
                        <span className="text-[10px] font-mono text-[var(--jr-text-3)] w-24 text-right shrink-0">
                            {r.replied}/{r.sent} · <span className={r.rate > 0 ? "text-[#22c55e]" : ""}>{r.rate}%</span>
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

export default function ReplyRatePanel() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(true);

    useEffect(() => {
        api.get("/outreach/analytics/reply-rates").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return null;
    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[var(--jr-surface)] px-5 py-4" data-testid="reply-rate-panel">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="reply-rate-toggle">
                <BarChart3 size={14} className="text-[var(--jr-brand)]" />
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)]">{i18n.t("replyrates.title")}</span>
                <span className="text-[10px] font-mono text-[var(--jr-text-3)]" data-testid="reply-rate-totals">
                    {data.totals.replied}/{data.totals.sent} {i18n.t("replyrates.replied_lbl")} · {data.totals.rate}%
                </span>
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="mt-3 grid md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-2">{i18n.t("replyrates.per_channel")}</div>
                        <RateRows rows={data.channels} kind="channel" testid="rates-channel" />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-2">{i18n.t("replyrates.per_signal")}</div>
                        <RateRows rows={data.signal_types} kind="signal" testid="rates-signal" />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-2">{i18n.t("replyrates.per_model")}</div>
                        <RateRows rows={data.models} kind="model" testid="rates-model" />
                        {(data.models || []).length > 0 && !(data.models || []).some((r) => r.key === "kimi") && (
                            <p className="mt-2 text-[10px] text-[var(--jr-text-3)] leading-relaxed" data-testid="rates-model-kimi-hint">
                                {i18n.t("replyrates.kimi_hint")}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
